import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AuthUser } from 'types/interfaces/user/AuthUser';

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  authenticated: boolean | null;
};

const initialState: AuthState = {
  user: null,
  loading: false,
  authenticated: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authStart(state) {
      state.loading = true;
    },
    authFailed(state) {
      state.loading = false;
    },
    authSuccess(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.loading = false;
    },
    setUser(state, action: PayloadAction<AuthUser | null>) {
      state.user = action.payload;
    },

    setAuthenticated(state, action: PayloadAction<boolean>) {
      state.authenticated = action.payload;
    },

    setUserUsernameWithAge(
      state,
      action: PayloadAction<Pick<AuthUser, 'username' | 'age'>>
    ) {
      if (!state.user) {
        return;
      }

      state.user = {
        ...state.user,
        username: action.payload.username,
        age: action.payload.age,
      };
    },

    setUserEmail(state: AuthState, action: PayloadAction<string>) {
      if (state.user) {
        state.user.email = action.payload;
      }
    },

    setUserEmailVerified(state: AuthState, action: PayloadAction<boolean>) {
      if (state.user) {
        state.user.email_verified = action.payload;
      }
    },

    setUserPhoto(state, action: PayloadAction<string>) {
      if (!state.user) {
        return;
      }

      state.user = {
        ...state.user,
        photo_url: action.payload,
      };
    },

    setUserPremiumSubscription(
      state: AuthState,
      action: PayloadAction<boolean>
    ) {
      if (state.user) {
        state.user.subscriptions.premium.is_premium = action.payload;
      }
    },

    setUserVipSubscription(state: AuthState, action: PayloadAction<boolean>) {
      if (state.user) {
        state.user.subscriptions.vip.is_vip = action.payload;
      }
    },
  },
});

export const {
  authStart,
  authFailed,
  authSuccess,
  setUser,
  setUserUsernameWithAge,
  setUserEmail,
  setUserEmailVerified,
  setUserPhoto,
  setUserPremiumSubscription,
  setUserVipSubscription,
  setAuthenticated,
} = authSlice.actions;

export const auth = authSlice.reducer;
